import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { useAsync } from 'react-use';

export type UserRole = 'admin' | 'dev' | 'secteam' | 'unknown';

// Security team members with access to security features
const SECURITY_TEAM_EMAILS = [
  'omar.garcia@spin.co',
  'jorge.beltran@spin.co',
  'rafael.vazquez@spin.co',
  'hector.soto@spin.co',
];

export interface UserRoleInfo {
  role: UserRole;
  groups: string[];
  isAdmin: boolean;
  isDev: boolean;
  isSecTeam: boolean;
  canAccessKafkaProd: boolean;
  canAccessDevinUsage: boolean;
  loading: boolean;
}

/**
 * Hook to get the current user's role based on Azure AD groups
 *
 * @returns UserRoleInfo with role, groups, and helper flags
 */
export const useUserRole = (): UserRoleInfo => {
  const identityApi = useApi(identityApiRef);

  const { value, loading } = useAsync(async () => {
    try {
      // Get user profile info
      const profileInfo = await identityApi.getProfileInfo();
      const userEmail = profileInfo.email?.toLowerCase() || '';

      // Check if user is in security team
      const isSecurityTeamMember = SECURITY_TEAM_EMAILS.map(e =>
        e.toLowerCase(),
      ).includes(userEmail);

      // Get user credentials with token
      const { token } = await identityApi.getCredentials();

      if (token) {
        // Decode JWT token (simple base64 decode, not verifying signature)
        const payload = JSON.parse(atob(token.split('.')[1]));
        const groups = payload.groups || [];
        const role = payload.role || 'unknown';

        // Don't override role - keep original role from token
        // isSecurityTeamMember is used separately for security page access

        return {
          role: role as UserRole,
          groups,
          isSecurityTeamMember,
          canAccessKafkaProd: payload.canAccessKafkaProd === true,
          canAccessDevinUsage: payload.canAccessDevinUsage === true,
        };
      }

      return {
        role: 'unknown' as UserRole,
        groups: [],
        isSecurityTeamMember,
      };
    } catch (error) {
      console.error('Error getting user role:', error);
      return {
        role: 'unknown' as UserRole,
        groups: [],
        isSecurityTeamMember: false,
      };
    }
  }, [identityApi]);

  const role = value?.role || 'unknown';
  const groups = value?.groups || [];
  const isSecurityTeamMember = value?.isSecurityTeamMember || false;

  return {
    role,
    groups,
    isAdmin: role === 'admin',
    canAccessKafkaProd: role === 'admin' || value?.canAccessKafkaProd === true,
    canAccessDevinUsage:
      role === 'admin' || value?.canAccessDevinUsage === true,
    isDev: role === 'dev',
    isSecTeam: role === 'secteam' || isSecurityTeamMember,
    loading: loading || false,
  };
};
